import type { StepType } from "@reactour/tour";
import { useEffect } from "react";
import { useTourStore } from "./tour-store";

export interface UseFeatureTourOptions {
  version?: number;
  autoStart?: boolean;
  delayMs?: number;
  startAt?: number;
}

export function useFeatureTour(
  tourId: string,
  steps: StepType[],
  options?: UseFeatureTourOptions,
) {
  const version = options?.version ?? 1;
  const autoStart = options?.autoStart ?? true;
  const delayMs = options?.delayMs ?? 600;

  const isTourCompleted = useTourStore((s) => s.isTourCompleted);
  const startTour = useTourStore((s) => s.startTour);
  const closeTour = useTourStore((s) => s.closeTour);
  const resetTour = useTourStore((s) => s.resetTour);
  const activeTourId = useTourStore((s) => s.activeTourId);
  const isOpen = useTourStore((s) => s.isOpen);
  const currentStep = useTourStore((s) => s.currentStep);

  const isCompleted = isTourCompleted(tourId, version);
  const isThisTourActive = activeTourId === tourId && isOpen;

  useEffect(() => {
    if (!autoStart || isCompleted) return;

    const timer = setTimeout(() => {
      startTour(tourId, steps, {
        version,
        startAt: options?.startAt ?? 0,
      });
    }, delayMs);

    return () => clearTimeout(timer);
  }, [
    tourId,
    steps,
    version,
    autoStart,
    isCompleted,
    delayMs,
    options?.startAt,
    startTour,
  ]);

  const start = (customSteps?: StepType[]) => {
    return startTour(tourId, customSteps ?? steps, {
      version,
      force: true,
      startAt: options?.startAt ?? 0,
    });
  };

  const reset = () => {
    resetTour(tourId);
  };

  return {
    start,
    close: closeTour,
    reset,
    isCompleted,
    isOpen: isThisTourActive,
    currentStep: isThisTourActive ? currentStep : 0,
  };
}
